/**
 * Servicio simplificado para generar comandos ESC/POS
 * No depende de bibliotecas externas que requieran módulos de Node.js
 */
export const simpleEscpos = {
  // Comandos ESC/POS básicos
  COMMANDS: {
    INIT: [0x1B, 0x40], // ESC @
    CUT: [0x1D, 0x56, 0x41, 0x10], // GS V A 16
    FEED: [0x1B, 0x64], // ESC d
    ALIGN_LEFT: [0x1B, 0x61, 0x00], // ESC a 0
    ALIGN_CENTER: [0x1B, 0x61, 0x01], // ESC a 1
    ALIGN_RIGHT: [0x1B, 0x61, 0x02], // ESC a 2
    BOLD_ON: [0x1B, 0x45, 0x01], // ESC E 1
    BOLD_OFF: [0x1B, 0x45, 0x00], // ESC E 0
    DOUBLE_HEIGHT_ON: [0x1B, 0x21, 0x10], // ESC ! 16
    DOUBLE_HEIGHT_OFF: [0x1B, 0x21, 0x00], // ESC ! 0
    UNDERLINE_ON: [0x1B, 0x2D, 0x01], // ESC - 1
    UNDERLINE_OFF: [0x1B, 0x2D, 0x00], // ESC - 0
  },

  /**
   * Convierte un string a un array de bytes
   * @param {string} text - Texto a convertir
   * @returns {Array} - Array de bytes
   */
  textToBytes(text) {
    const bytes = [];
    for (let i = 0; i < text.length; i++) {
      bytes.push(text.charCodeAt(i));
    }
    return bytes;
  },

  /**
   * Concatena arrays de bytes
   * @param {...Array} arrays - Arrays de bytes a concatenar
   * @returns {Array} - Array de bytes concatenado
   */
  concat(...arrays) {
    return arrays.reduce((result, array) => result.concat(array), []);
  },

  /**
   * Convierte un array de bytes a base64
   * @param {Array} bytes - Array de bytes
   * @returns {string} - String en formato base64
   */
  bytesToBase64(bytes) {
    // Convertir array de bytes a string binario
    let binary = '';
    const len = bytes.length;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    // Convertir string binario a base64
    return window.btoa(binary);
  },

  /**
   * Genera un ticket de prueba
   * @param {Object} business - Información del negocio
   * @returns {string} - Comandos ESC/POS en formato base64
   */
  generateTestTicket(business) {
    let bytes = [];
    
    // Inicializar impresora
    bytes = this.concat(bytes, this.COMMANDS.INIT);
    
    // Encabezado
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_CENTER);
    bytes = this.concat(bytes, this.COMMANDS.BOLD_ON);
    bytes = this.concat(bytes, this.textToBytes('=== PRUEBA ESC/POS ===\n'));
    bytes = this.concat(bytes, this.textToBytes((business?.name?.toUpperCase() || 'TIBO APP') + '\n'));
    bytes = this.concat(bytes, this.COMMANDS.BOLD_OFF);
    bytes = this.concat(bytes, this.textToBytes('================================\n\n'));
    
    // Información
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_LEFT);
    bytes = this.concat(bytes, this.textToBytes(`Business ID: ${business?.id}\n`));
    bytes = this.concat(bytes, this.textToBytes(`Fecha: ${new Date().toLocaleString()}\n`));
    bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
    
    // Tabla de ejemplo
    bytes = this.concat(bytes, this.COMMANDS.BOLD_ON);
    bytes = this.concat(bytes, this.textToBytes('PRODUCTO      CANT    PRECIO    TOTAL\n'));
    bytes = this.concat(bytes, this.COMMANDS.BOLD_OFF);
    bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
    
    // Productos de ejemplo
    bytes = this.concat(bytes, this.textToBytes('Café Americano 2     $45.00   $90.00\n'));
    bytes = this.concat(bytes, this.textToBytes('Croissant     1     $35.00   $35.00\n'));
    bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
    
    // Totales
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_RIGHT);
    bytes = this.concat(bytes, this.textToBytes('SUBTOTAL:             $125.00\n'));
    bytes = this.concat(bytes, this.textToBytes('TOTAL:                $125.00\n\n'));
    
    // Pie de página
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_CENTER);
    bytes = this.concat(bytes, this.textToBytes('¡Gracias por su compra!\n'));
    bytes = this.concat(bytes, this.textToBytes('www.tibo.app\n\n\n'));
    
    // Cortar papel
    bytes = this.concat(bytes, this.COMMANDS.CUT);
    
    // Convertir a base64
    return this.bytesToBase64(bytes);
  },

  /**
   * Genera un ticket de venta
   * @param {Object} options - Opciones para generar el ticket
   * @returns {string} - Comandos ESC/POS en formato base64
   */
  generateSaleTicket(options) {
    const { business, sale, items, customer, type = 'delivery' } = options;
    let bytes = [];
    
    // Inicializar impresora
    bytes = this.concat(bytes, this.COMMANDS.INIT);
    
    // Encabezado
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_CENTER);
    bytes = this.concat(bytes, this.COMMANDS.BOLD_ON);
    bytes = this.concat(bytes, this.textToBytes((business?.name?.toUpperCase() || 'TIBO APP') + '\n'));
    bytes = this.concat(bytes, this.COMMANDS.BOLD_OFF);
    bytes = this.concat(bytes, this.textToBytes('================================\n'));
    
    // Información de la venta
    bytes = this.concat(bytes, this.textToBytes(`VENTA #${sale.display_id || sale.id.slice(-8)}\n`));
    bytes = this.concat(bytes, this.textToBytes(`${new Date().toLocaleString()}\n`));
    bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
    
    // Contenido según el tipo de ticket
    switch (type) {
      case 'delivery':
        bytes = this._generateDeliveryTicket(bytes, items, sale);
        break;
      case 'fulfillment':
        bytes = this._generateFulfillmentTicket(bytes, items, sale);
        break;
      case 'backup':
        bytes = this._generateBackupTicket(bytes, items, sale);
        break;
      default:
        bytes = this._generateDeliveryTicket(bytes, items, sale);
    }
    
    // Información del cliente
    if (customer?.name) {
      bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
      bytes = this.concat(bytes, this.textToBytes(`CLIENTE: ${customer.name}\n`));
      if (customer.phone) {
        bytes = this.concat(bytes, this.textToBytes(`TELÉFONO: ${customer.phone}\n`));
      }
      if (customer.address) {
        bytes = this.concat(bytes, this.textToBytes(`DIRECCIÓN: ${customer.address}\n`));
      }
    }
    
    // Pie de página
    bytes = this.concat(bytes, this.COMMANDS.FEED, [1]);
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_CENTER);
    bytes = this.concat(bytes, this.textToBytes('¡Gracias por su compra!\n'));
    bytes = this.concat(bytes, this.textToBytes('www.tibo.app\n\n\n'));
    
    // Cortar papel
    bytes = this.concat(bytes, this.COMMANDS.CUT);
    
    // Convertir a base64
    return this.bytesToBase64(bytes);
  },

  /**
   * Genera el contenido de un ticket de entrega
   * @private
   */
  _generateDeliveryTicket(bytes, items, sale) {
    // Tabla de productos
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_LEFT);
    bytes = this.concat(bytes, this.COMMANDS.BOLD_ON);
    bytes = this.concat(bytes, this.textToBytes('PRODUCTO      CANT    PRECIO    TOTAL\n'));
    bytes = this.concat(bytes, this.COMMANDS.BOLD_OFF);
    bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
    
    // Productos
    items?.forEach(item => {
      const productName = item.title?.substring(0, 14) || 'Producto';
      const quantity = item.quantity?.toString().padEnd(4) || '1   ';
      const unitPrice = (item.unit_price || 0).toFixed(2).padEnd(8);
      const total = (item.quantity * item.unit_price || 0).toFixed(2);
      bytes = this.concat(bytes, this.textToBytes(`${productName}${quantity}${unitPrice}${total}\n`));
    });
    
    // Totales
    bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_RIGHT);
    bytes = this.concat(bytes, this.textToBytes(`SUBTOTAL:             $${sale.total_amount?.toFixed(2) || '0.00'}\n`));
    
    if (sale.total_deposit_amount > 0) {
      bytes = this.concat(bytes, this.textToBytes(`SEÑA ENVASES:         $${sale.total_deposit_amount?.toFixed(2)}\n`));
    }
    
    bytes = this.concat(bytes, this.COMMANDS.BOLD_ON);
    bytes = this.concat(bytes, this.textToBytes(`TOTAL:                $${(sale.total_amount + (sale.total_deposit_amount || 0)).toFixed(2)}\n`));
    bytes = this.concat(bytes, this.COMMANDS.BOLD_OFF);
    
    return bytes;
  },

  /**
   * Genera el contenido de un ticket de preparación
   * @private
   */
  _generateFulfillmentTicket(bytes, items, sale) {
    // Tabla de productos
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_LEFT);
    bytes = this.concat(bytes, this.COMMANDS.BOLD_ON);
    bytes = this.concat(bytes, this.textToBytes('PRODUCTO                  CANTIDAD\n'));
    bytes = this.concat(bytes, this.COMMANDS.BOLD_OFF);
    bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
    
    // Productos (solo nombre y cantidad)
    items?.forEach(item => {
      const productName = item.title?.substring(0, 25) || 'Producto';
      const quantity = item.quantity?.toString() || '1';
      const paddedName = productName.padEnd(25);
      bytes = this.concat(bytes, this.textToBytes(`${paddedName}${quantity}\n`));
    });
    
    // Notas
    if (sale.notes) {
      bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
      bytes = this.concat(bytes, this.textToBytes('NOTAS:\n'));
      bytes = this.concat(bytes, this.textToBytes(`${sale.notes}\n`));
    }
    
    return bytes;
  },

  /**
   * Genera el contenido de un ticket de respaldo
   * @private
   */
  _generateBackupTicket(bytes, items, sale) {
    // Tabla de productos
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_LEFT);
    bytes = this.concat(bytes, this.COMMANDS.BOLD_ON);
    bytes = this.concat(bytes, this.textToBytes('PRODUCTO      CANT    PRECIO    TOTAL\n'));
    bytes = this.concat(bytes, this.COMMANDS.BOLD_OFF);
    bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
    
    // Productos
    items?.forEach(item => {
      const productName = item.title?.substring(0, 14) || 'Producto';
      const quantity = item.quantity?.toString().padEnd(4) || '1   ';
      const unitPrice = (item.unit_price || 0).toFixed(2).padEnd(8);
      const total = (item.quantity * item.unit_price || 0).toFixed(2);
      bytes = this.concat(bytes, this.textToBytes(`${productName}${quantity}${unitPrice}${total}\n`));
    });
    
    // Totales
    bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
    bytes = this.concat(bytes, this.COMMANDS.ALIGN_RIGHT);
    bytes = this.concat(bytes, this.textToBytes(`SUBTOTAL:             $${sale.total_amount?.toFixed(2) || '0.00'}\n`));
    
    if (sale.total_deposit_amount > 0) {
      bytes = this.concat(bytes, this.textToBytes(`SEÑA ENVASES:         $${sale.total_deposit_amount?.toFixed(2)}\n`));
    }
    
    bytes = this.concat(bytes, this.COMMANDS.BOLD_ON);
    bytes = this.concat(bytes, this.textToBytes(`TOTAL:                $${(sale.total_amount + (sale.total_deposit_amount || 0)).toFixed(2)}\n`));
    bytes = this.concat(bytes, this.COMMANDS.BOLD_OFF);
    
    // Información de pago
    if (sale.payment_method) {
      bytes = this.concat(bytes, this.textToBytes('--------------------------------\n'));
      bytes = this.concat(bytes, this.COMMANDS.ALIGN_LEFT);
      bytes = this.concat(bytes, this.textToBytes(`MÉTODO DE PAGO: ${sale.payment_method}\n`));
      if (sale.payment_reference) {
        bytes = this.concat(bytes, this.textToBytes(`REFERENCIA: ${sale.payment_reference}\n`));
      }
    }
    
    return bytes;
  }
}; 